import {
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react';

import { Model } from 'rwl-shared';
import { useQueryParams } from './useQueryParams';
import { useFetchUser } from './useFetchUser';
import { useGlobal } from './useGlobal';
import { removeFromURL } from './utils';
import { setSentryUser } from '../sentry';
export const TECHNICIAN_URL_KEY = 'technicianId';
export const STORAGE_USERID_KEY = 'rwl:user:id';
const TechnicianContext = createContext<{
    technician: Model.PortfolioUser | undefined;
    isTechnicianReady: boolean;
}>({
    technician: undefined,
    isTechnicianReady: false,
});

export const TechnicianProvider = ({
    children,
}: {
    children: ReactNode;
}): JSX.Element | null => {
    const { accountId } = useGlobal();
    const [technician, setTechnician] = useState<
        Model.PortfolioUser | undefined
    >(undefined);
    const [isTechnicianReady, setIsReady] = useState<boolean>(false);
    const params = useQueryParams();
    const technicianId =
        params?.get(TECHNICIAN_URL_KEY) ??
        localStorage.getItem(STORAGE_USERID_KEY);
    const { data: user, isLoading: isLoadingUser } = useFetchUser({
        accountId: accountId,
        userId: technicianId ?? undefined,
    });
    useEffect(() => {
        // not ready yet
        if (isLoadingUser) {
            return;
        }

        // done and we either have a user or don't at this point
        if (user) {
            setTechnician(user);
            setIsReady(true);
            removeFromURL(TECHNICIAN_URL_KEY);
            setSentryUser(user.id, user.email);
        } else {
            setIsReady(true);
        }
        // write it to the local storage for future use
        if (technicianId) {
            localStorage.setItem(STORAGE_USERID_KEY, technicianId);
        }
    }, [user, isLoadingUser]);

    return (
        <TechnicianContext.Provider
            value={{
                technician,
                isTechnicianReady: isTechnicianReady,
            }}
        >
            {children}
        </TechnicianContext.Provider>
    );
};

export const useTechnician = () => {
    const context = useContext(TechnicianContext);
    if (context === undefined) {
        throw new Error(
            'useTechnician must be used within a TechnicianProvider'
        );
    }
    return context!;
};
